import React, { useMemo, useEffect } from 'react';
import { format as formatDate, parse as parseDate, isValid } from 'date-fns';
import useTranslate from '../../Hooks/useTranslate';
import { TextField, Box } from '@mui/material';
import { DynamicSelect } from './DynamicSelect';
import { AsyncSelect } from './AsyncSelect';
import DbeDatePicker from '../Inputs/DbeDatePicker';
import Switch from '../Inputs/FormSwitch';
import { ReadOnlyValue } from './ReadOnlyValue';
import { useFieldValue, useFieldAtom, getFullPath } from './useFieldValue';

/** The empty value of a select, kept here so seeding a multiple one does not rebuild its list. */
const NO_SELECTION = [];

export const StringField = ({ basePath, field }) => {
  const translate = useTranslate();
  const [value, setValue] = useFieldValue(basePath, field, '');

  if (field.readOnly)
    return <ReadOnlyValue id={field.key} label={field.label} value={value} />;

  return (
    <TextField
      fullWidth
      type="text"
      label={translate(field.label)}
      value={value ?? ''}
      onChange={(e) =>
        setValue(
          field.upperCase ? e.target.value.toUpperCase() : e.target.value
        )
      }
      required={field.required}
      size="small"
      margin="normal"
      slotProps={{ htmlInput: { maxLength: field.maxLength } }}
    />
  );
};

export const NumberField = ({ basePath, field }) => {
  const translate = useTranslate();
  const [value, setValue] = useFieldValue(basePath, field);

  // Holds a typed number inside the declared bounds.
  useEffect(() => {
    if (value !== undefined && value !== null && value !== '') {
      if (field.min !== undefined && value < field.min) {
        setValue(field.min);
      } else if (field.max !== undefined && value > field.max) {
        setValue(field.max);
      }
    }
  }, [field.min, field.max, value, setValue]);

  if (field.readOnly)
    return <ReadOnlyValue id={field.key} label={field.label} value={value} />;

  return (
    <TextField
      fullWidth
      type="number"
      label={translate(field.label)}
      value={value ?? ''}
      onChange={(e) =>
        setValue(e.target.value === '' ? '' : Number(e.target.value))
      }
      required={field.required}
      size="small"
      margin="normal"
      slotProps={{ htmlInput: { min: field.min, max: field.max } }}
    />
  );
};

export const BooleanField = ({ basePath, field }) => {
  const translate = useTranslate();
  const [value, setValue] = useFieldValue(basePath, field, false);

  if (field.readOnly)
    return <ReadOnlyValue id={field.key} label={field.label} value={!!value} />;

  // Wrapped in the margins the other fields carry, so a flag sits on the same baseline as the
  // select or date beside it instead of at the top of the row.
  return (
    <Box sx={{ mt: 2, mb: 1 }}>
      <Switch
        id={field.key}
        checked={!!value}
        onChange={(e) => setValue(e.target.checked)}
        label={translate(field.label)}
      />
    </Box>
  );
};

/**
 * A choice from a list: the one the field declares, the one its registry key answers with, or the
 * one that key answers as text is typed.
 *
 * `searchable` is the difference between asking once for the whole list and asking again per
 * keystroke, which is a property of the list's size rather than of the control.
 */
export const SelectField = ({ basePath, field }) => {
  const [value, setValue] = useFieldValue(
    basePath,
    field,
    field.multiple ? NO_SELECTION : ''
  );

  if (field.readOnly)
    return <ReadOnlyValue id={field.key} label={field.label} value={value} />;

  if (field.searchable)
    return (
      <AsyncSelect
        field={field}
        value={value}
        onChange={setValue}
        basePath={basePath}
      />
    );

  return (
    <DynamicSelect
      field={field}
      value={value}
      onChange={setValue}
      basePath={basePath}
    />
  );
};

// Superseded by `select` with `searchable`. Kept so a descriptor saved naming it still renders.
export const AsyncSelectField = ({ field, ...rest }) => (
  <SelectField {...rest} field={{ ...field, searchable: true }} />
);

// The store keeps the date as text in `field.format`, so the payload stays
// JSON-serialisable and round-trips through the API unchanged.
export const DateField = ({ basePath, field }) => {
  const translate = useTranslate();
  const pattern = field.format || 'dd/MM/yyyy';
  const [value, setValue] = useFieldValue(basePath, field);

  const selectedDate = useMemo(() => {
    if (!value) return null;
    const parsed = parseDate(value, pattern, new Date());
    return isValid(parsed) ? parsed : null;
  }, [value, pattern]);

  const handleChange = (newDate) => {
    setValue(newDate && isValid(newDate) ? formatDate(newDate, pattern) : null);
  };

  if (field.readOnly)
    return <ReadOnlyValue id={field.key} label={field.label} value={value} />;

  return (
    <Box sx={{ mt: 2, mb: 1 }}>
      <DbeDatePicker
        variant="outlined"
        label={translate(field.label)}
        date={selectedDate}
        onChange={handleChange}
        inputFormat={pattern}
        views={field.views || null}
      />
    </Box>
  );
};

/** A value the schema carries rather than collects; shown, and written to the store as declared. */
export const ConstantField = ({ basePath, field }) => {
  const [value, setValue] = useFieldAtom(getFullPath(basePath, field.key));

  useEffect(() => {
    const currentValueString = JSON.stringify(value ?? null);
    const expectedValueString = JSON.stringify(field.value ?? null);

    if (currentValueString !== expectedValueString) {
      setValue(field.value, { isInit: true });
    }
  }, [field.value, value, setValue]);

  return <ReadOnlyValue id={field.key} label={field.label} value={value} />;
};
